import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Page, PageHeader, Modal, Field, inputCls, PrimaryButton, DangerButton, EmptyState } from "@/components/lims";
import { Plus, Snowflake, Trash, Stack, Package, CaretRight } from "@phosphor-icons/react";
import { canManageStorage, canDeleteStorage } from "@/lib/roles";
import RenameField from "@/components/RenameField";

export default function Storage() {
  const { user } = useAuth();
  const [searchParams] = useSearchParams();
  const canManage = canManageStorage(user);
  const canDelete = canDeleteStorage(user);
  const [freezers, setFreezers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modal, setModal] = useState(null); // 'freezer' | {type:'rack',freezer} | {type:'box',rack}
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);
  const [retireTarget, setRetireTarget] = useState(null);
  const [retireReason, setRetireReason] = useState("");

  const load = async () => {
    setLoading(true);
    const { data } = await api.get("/freezers");
    setFreezers(data);
    setLoading(false);
  };
  useEffect(() => { load(); }, []);

  // A location path links here to point at one freezer or rack (UX-07). The
  // map has no page per level, so the query string focuses a level instead —
  // the same shape the box page uses for `?position=`. Scrolling waits on the
  // tree: the element does not exist until the freezers land.
  const focusFreezer = searchParams.get("freezer");
  const focusRack = searchParams.get("rack");
  useEffect(() => {
    if (loading || freezers.length === 0) return;
    const target = focusRack ? `rack-${focusRack}`
      : focusFreezer ? `freezer-${focusFreezer}` : null;
    if (!target) return;
    document.getElementById(target)?.scrollIntoView({
      behavior: "smooth", block: "center",
    });
  }, [loading, freezers, focusFreezer, focusRack]);

  // Reload rather than patch the local tree: a rename changes the derived
  // location label on every sample below it, and the server is the only place
  // that walk happens.
  const rename = async (collection, id, name) => {
    try {
      await api.patch(`/${collection}/${id}`, { name });
      await load();
      toast.success(`Renamed to ${name}`);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
      throw err;
    }
  };

  const openFreezer = () => { setForm({ name: "", temperature: "-80°C", location: "" }); setModal({ type: "freezer" }); };
  const openRack = (freezer) => { setForm({ name: "" }); setModal({ type: "rack", freezer }); };
  const openBox = (rack) => { setForm({ name: "", rows: 9, cols: 9 }); setModal({ type: "box", rack }); };

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      if (modal.type === "freezer") await api.post("/freezers", form);
      if (modal.type === "rack") await api.post("/racks", { ...form, freezer_id: modal.freezer.id });
      if (modal.type === "box") await api.post("/boxes", { ...form, rack_id: modal.rack.id, rows: parseInt(form.rows), cols: parseInt(form.cols) });
      toast.success("Created");
      setModal(null);
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally { setSaving(false); }
  };

  const doRetire = async () => {
    if (!retireReason.trim()) { toast.error("A reason is required to retire a freezer"); return; }
    setSaving(true);
    try {
      const { data } = await api.delete(`/freezers/${retireTarget.id}`, {
        data: { reason: retireReason },
      });
      toast.success(`${retireTarget.name} retired · ${data.boxes_retired} box(es)`);
      setRetireTarget(null);
      setRetireReason("");
      load();
    } catch (err) {
      // 409 means samples are still stored here — surface the server's count
      // rather than a generic failure, since it tells the user what to do next.
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally { setSaving(false); }
  };


  return (
    <Page>
      <PageHeader
        title="Storage"
        subtitle="A visual map of freezers, racks, and boxes"
        action={canManage && <PrimaryButton data-testid="new-freezer-button" onClick={openFreezer}><Plus size={18} /> Add Freezer</PrimaryButton>}
      />

      {loading ? (
        <div className="grid md:grid-cols-2 gap-4">{[...Array(2)].map((_, i) => <div key={i} className="h-48 bg-white border border-slate-200 rounded-lg animate-pulse" />)}</div>
      ) : freezers.length === 0 ? (
        <div className="bg-white rounded-lg border border-slate-200"><EmptyState icon={Snowflake} title="No freezers yet" subtitle="Add a freezer to start mapping storage" /></div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {freezers.map((f) => (
            <div
              key={f.id}
              id={`freezer-${f.id}`}
              data-testid={`freezer-${f.name}`}
              className={`rounded-lg border bg-white p-6 transition-shadow ${
                focusFreezer === f.id
                  ? "border-teal-600 ring-2 ring-teal-600/40"
                  : "border-slate-200"
              }`}
            >
              <div className="flex items-start justify-between mb-4">
                <div className="flex items-center gap-3">
                  <div className="w-11 h-11 rounded-md bg-status-info/10 border border-status-info/30 flex items-center justify-center">
                    <Snowflake size={24} weight="duotone" className="text-status-info" />
                  </div>
                  <div>
                    <RenameField
                      value={f.name}
                      label={`freezer ${f.name}`}
                      testid={`rename-freezer-${f.name}`}
                      onRename={canManage ? (name) => rename("freezers", f.id, name) : null}
                      className="font-display font-semibold text-slate-900"
                    />
                    <div className="text-xs text-slate-400">{f.temperature}{f.location ? ` · ${f.location}` : ""}</div>
                  </div>
                </div>
                <div className="flex items-center gap-1">
                  {canManage && <button onClick={() => openRack(f)} className="text-xs px-2 py-1 rounded-md text-blue-600 hover:bg-blue-50 font-medium transition-colors flex items-center gap-1"><Plus size={14} /> Rack</button>}
                  {canDelete && <button onClick={() => { setRetireTarget(f); setRetireReason(""); }} title="Retire freezer" className="p-1.5 rounded-md text-slate-400 hover:text-crit hover:bg-crit-bg transition-colors"><Trash size={16} /></button>}
                </div>
              </div>

              {f.racks?.length === 0 ? (
                <p className="text-sm text-slate-400">No racks. Add one to store boxes.</p>
              ) : (
                <div className="space-y-3">
                  {f.racks.map((r) => (
                    <div
                      key={r.id}
                      id={`rack-${r.id}`}
                      data-testid={`rack-${r.name}`}
                      className={`rounded-md border p-3 transition-shadow ${
                        focusRack === r.id
                          ? "border-teal-600 ring-2 ring-teal-600/40"
                          : "border-slate-200"
                      }`}
                    >
                      <div className="flex items-center justify-between mb-2">
                        <div className="flex min-w-0 items-center gap-2 text-sm font-medium text-slate-700">
                          <Stack size={16} weight="duotone" className="shrink-0 text-slate-400" />
                          <RenameField
                            value={r.name}
                            label={`rack ${r.name}`}
                            testid={`rename-rack-${r.name}`}
                            onRename={canManage ? (name) => rename("racks", r.id, name) : null}
                          />
                        </div>
                        {canManage && <button onClick={() => openBox(r)} className="text-xs text-blue-600 hover:underline flex items-center gap-0.5"><Plus size={12} /> Box</button>}
                      </div>
                      {r.boxes?.length === 0 ? (
                        <p className="text-xs text-slate-400 pl-1">No boxes</p>
                      ) : (
                        <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                          {r.boxes.map((b) => {
                            const pct = b.capacity ? Math.round((b.occupied / b.capacity) * 100) : 0;
                            return (
                              <Link key={b.id} to={`/storage/boxes/${b.id}`} data-testid={`box-${b.name}`} className="block text-left border border-slate-200 rounded-md p-2.5 hover:border-slate-900 hover:-translate-y-0.5 transition-all duration-200 bg-slate-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-teal-600">
                                <div className="flex items-center gap-1.5 text-xs font-medium text-slate-700"><Package size={14} weight="duotone" className="text-slate-400" /> {b.name}</div>
                                <div className="mt-1.5 h-1.5 rounded-full bg-slate-200 overflow-hidden">
                                  <div className="h-full bg-teal-600 transition-all" style={{ width: `${pct}%` }} />
                                </div>
                                <div className="mt-1 text-[10px] text-slate-400">{b.occupied}/{b.capacity} used</div>
                              </Link>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Create modal */}
      <Modal open={!!modal} onClose={() => setModal(null)} title={modal?.type === "freezer" ? "Add Freezer" : modal?.type === "rack" ? "Add Rack" : "Add Box"} testid="storage-modal">
        <form onSubmit={submit} className="space-y-4">
          <Field label="Name"><input data-testid="storage-name-input" required value={form.name || ""} onChange={set("name")} className={inputCls} placeholder={modal?.type === "box" ? "e.g. Box A" : modal?.type === "rack" ? "e.g. Rack 1" : "e.g. Freezer -80 #1"} /></Field>
          {modal?.type === "freezer" && (
            <div className="grid grid-cols-2 gap-4">
              <Field label="Temperature"><input value={form.temperature || ""} onChange={set("temperature")} className={inputCls} /></Field>
              <Field label="Location"><input value={form.location || ""} onChange={set("location")} className={inputCls} placeholder="Room 204" /></Field>
            </div>
          )}
          {modal?.type === "box" && (
            <div className="grid grid-cols-2 gap-4">
              <Field label="Rows"><input type="number" min="1" value={form.rows || 9} onChange={set("rows")} className={inputCls} /></Field>
              <Field label="Columns"><input type="number" min="1" value={form.cols || 9} onChange={set("cols")} className={inputCls} /></Field>
            </div>
          )}
          <div className="flex justify-end gap-2 pt-2">
            <button type="button" onClick={() => setModal(null)} className="px-4 py-2 rounded-md text-sm text-slate-600 hover:bg-slate-100 transition-colors">Cancel</button>
            <PrimaryButton data-testid="save-storage-button" disabled={saving}>Create</PrimaryButton>
          </div>
        </form>
      </Modal>

      {/* Retire freezer */}
      <Modal open={!!retireTarget} onClose={() => setRetireTarget(null)}
             title={retireTarget ? `Retire ${retireTarget.name}` : ""} testid="retire-freezer-modal">
        {retireTarget && (
          <div className="space-y-4">
            <p className="text-sm text-slate-600">
              Retiring removes this freezer, its racks and its boxes from the storage map.
              Nothing is erased. The records are kept and the action is written to the audit trail.
            </p>
            <p className="text-sm text-slate-600">
              A freezer that still holds samples cannot be retired. Move them first.
            </p>
            <Field label="Reason">
              <textarea data-testid="retire-reason-input" value={retireReason}
                        onChange={(e) => setRetireReason(e.target.value)} rows={2}
                        className={inputCls} placeholder="e.g. Unit decommissioned, contents relocated to -80 002" />
            </Field>
            <div className="flex justify-end gap-2 pt-1">
              <button type="button" onClick={() => setRetireTarget(null)}
                      className="px-4 py-2 rounded-md text-sm text-slate-600 hover:bg-slate-100 transition-colors">Cancel</button>
              <DangerButton data-testid="confirm-retire-button" onClick={doRetire} disabled={saving}>
                <Trash size={16} /> Retire freezer
              </DangerButton>
            </div>
          </div>
        )}
      </Modal>

      {/* Box grid view */}
    </Page>
  );
}

